<script>
import isEqual from 'lodash/isEqual';
import CalendarSlotCopy from '@components/programs/calendar-slots/calendar-slot-copy.vue';
import CalendarSlotTooltip from '@components/programs/calendar-slots/calendar-slot-tooltip.vue';
import DishesSlot from '@components/plan/dishes-slot.vue';
import DishesSlotTooltipList from '@components/plan/dishes-slot-tooltip-list.vue';
import NotificationProgramCalendar from '@components/notifications/notification-program-calendar.vue';
import {
  platelistComputed,
  platelistMethods,
  recipeComputed,
  recipeMethods,
} from '@state/helpers';

export default {
  components: {
    CalendarSlotCopy,
    CalendarSlotTooltip,
    DishesSlot,
    DishesSlotTooltipList,
    NotificationProgramCalendar,
  },
  props: {
    planWeek: {
      type: [Object, Array],
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      disableAddingDish: false,
      hoveredMealtimeSlot: null,
      showTooltip: false,
      showCopyIcon: false,
    };
  },
  computed: {
    ...platelistComputed,
    ...recipeComputed,
    planDays() {
      return (this.planWeek && this.planWeek.planDays) || this.planWeek;
    },
    isPlatelistLocked() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.editabilityStatus &&
        this.selectedPlatelist.editabilityStatus.locked
      );
    },
  },
  mounted() {},
  methods: {
    ...platelistMethods,
    ...recipeMethods,
    mealtimeLeftovers(leftovers, mealtime) {
      if (leftovers && leftovers.length) {
        switch (mealtime) {
          case 'Breakfast':
            return leftovers.filter(
              (leftover) => leftover.mealTime.name === mealtime
            );
          case 'Lunch':
            return leftovers.filter(
              (leftover) => leftover.mealTime.name === mealtime
            );
          case 'Dinner':
            return leftovers.filter(
              (leftover) => leftover.mealTime.name === mealtime
            );
        }
      } else {
        return [];
      }
    },
    getSelectedSlotDishes() {
      const leftovers = this.mealtimeLeftovers(
        this.selectedPlanDay.leftovers,
        this.selectedMealtime.headerMealTime
      );
      return this.selectedMealtime.recipes
        .concat(this.selectedMealtime.meals)
        .concat(leftovers);
    },
    slotHasDishes(day, mealtime) {
      return (
        day[mealtime].dishCount > 0 ||
        this.mealtimeLeftovers(day.leftovers, mealtime).length > 0
      );
    },
    toggleTooltip(day, mealtime) {
      if (!this.showTooltip) {
        this.showTooltip = true;
        this.setSelectedPlanDay(day);
        this.setSelectedMealtime(mealtime);
      } else {
        this.showTooltip = false;
        this.setSelectedPlanDay(null);
        this.setSelectedMealtime(null);
      }
    },
    handleCancelDupe() {
      if (this.isAddingDish) {
        this.setIsAddingDish(false);
        this.setSelectedMealtimeDish(null);
      } else {
        this.showCopyIcon = false;
      }
    },
    isSelectedMealtime(day, mealtime) {
      return (
        this.selectedPlanDay &&
        this.selectedPlanDay.id === day.id &&
        this.hoveredMealtimeSlot &&
        this.hoveredMealtimeSlot.headerMealTime === mealtime.headerMealTime
      );
    },
    isHoveredMealtime(mealtime) {
      return isEqual(this.hoveredMealtimeSlot, mealtime);
    },
    manageRecipe(recipeData) {
      if (!this.showCopyIcon) {
        this.setSelectedPlanDay(recipeData.day);
        this.setSelectedMealtime(recipeData.mealtime);
        this.$emit('manage-recipe', recipeData.recipe);
      }
    },
    dayNum(day) {
      return day.header.substring(4);
    },
    onActivateCopy(recipe) {
      this.showCopyIcon = true;
    },
    async addDish(addToDay, addToMealtime) {
      if (!this.selectedMealtimeDish || !this.selectedMealtimeDish.id) {
        this.setSelectedMealtimeDish(null);
        this.setIsAddingDish(false);
        return;
      }
      this.setIsAddingDish(false);
      this.setIsSavingDish(true);
      await this.addMealToPlatelist({
        platelistDayId: addToDay.id,
        mealTimeId: addToMealtime.id,
        recipeId: this.selectedMealtimeDish ? this.selectedMealtimeDish.id : '',
        useLeftovers: this.selectedMealtimeDish
          ? this.selectedMealtimeDish.hasLeftovers
          : '',
      });
      await this.getAdminPlanGrid({
        platelistId: this.selectedPlatelist.id,
        weekOffset: this.selectedWeekOffset,
      });
      this.setSelectedMealtimeDish(null);
      this.setIsSavingDish(false);
    },
    async copyMeal(copyToDay, copyToMealtime) {
      this.showCopyIcon = false;
      const dishes = this.getSelectedSlotDishes();
      if (dishes.length) {
        for (const dish of dishes) {
          await this.addMealToPlatelist({
            platelistDayId: copyToDay.id,
            mealTimeId: copyToMealtime.id,
            recipeId: dish.id,
            useLeftovers: dish.hasLeftovers,
          });
        }
        this.getAdminPlanGrid({
          platelistId: this.selectedPlatelist.id,
          weekOffset: this.selectedWeekOffset,
        });
      } else {
        for (const dish of dishes) {
          await this.removeMealFromPlatelist(dish.platelistMealId);
        }
        this.getAdminPlanGrid({
          platelistId: this.selectedPlatelist.id,
          weekOffset: this.selectedWeekOffset,
        });
      }
    },
    onApplyMeal(applyToDay, applyToMealtime) {
      this.isAddingDish
        ? this.addDish(applyToDay, applyToMealtime)
        : this.copyMeal(applyToDay, applyToMealtime);
    },
    async onRemoveMeal() {
      const dishes = this.getSelectedSlotDishes();
      if (dishes.length) {
        for (const dish of dishes) {
          await this.removeMealFromPlatelist(dish.platelistMealId);
        }
        this.getAdminPlanGrid({
          platelistId: this.selectedPlatelist.id,
          weekOffset: this.selectedWeekOffset,
        });
      }
    },
    onHoveredMealtimeSlot(day, mealtime) {
      if (!this.isSavingDish) {
        this.hoveredMealtimeSlot = mealtime;

        if (this.isAddingDish && this.selectedMealtimeDish.hasLeftovers) {
          const hoveredDayNum = mealtime.headerDate.charAt(4);
          const hoveredMealTime = mealtime.headerMealTime;
          const dishDayNum = this.selectedMealtimeDish.preparedOnDate.charAt(4);
          const dishMealTime = this.selectedMealtimeDish.mealTime.name;

          if (
            hoveredDayNum < dishDayNum ||
            (hoveredDayNum === dishDayNum && hoveredMealTime === dishMealTime)
          ) {
            this.disableAddingDish = true;
          } else {
            this.disableAddingDish = false;
          }
        }
      }
    },
  },
};
</script>

<template>
  <div class="plan-grid">
    <NotificationProgramCalendar
      v-if="showCopyIcon || isAddingDish"
      :content="'day'"
      :isAddingDish="isAddingDish"
      @cancel-duplication="handleCancelDupe"
    />

    <div class="plan-grid-row">
      <div class="day-col" v-for="(day, i) in planDays" :key="i">
        <div class="day-header">
          <p class="day-num">{{ day.Breakfast.headerDate || dayNum(day) }}</p>
        </div>
        <div
          class="mealtime-slot morning"
          @mouseover="onHoveredMealtimeSlot(day, day.Breakfast)"
          @mouseleave="showTooltip = false"
        >
          <span class="mealtime-tag" v-if="i === 0">
            <img class="icon-mealtime" src="@assets/images/icon-sunrise.svg" />
            <label>Morning</label>
          </span>
          <div
            :class="[
              'mealtime-option-bar',
              { active: showTooltip && !showCopyIcon && !isAddingDish },
            ]"
            v-if="!isPlatelistLocked && slotHasDishes(day, 'Breakfast')"
          >
            <span
              class="icon-add-wrapper"
              @click="toggleTooltip(day, day.Breakfast)"
              >...</span
            >
          </div>

          <CalendarSlotTooltip
            v-if="
              showTooltip &&
                !showCopyIcon &&
                isSelectedMealtime(day, day.Breakfast)
            "
          >
            <DishesSlotTooltipList
              :day="day"
              :mealtime="day.Breakfast"
              :slotHasDishes="slotHasDishes(day, 'Breakfast')"
              @activate-copy="onActivateCopy"
              @remove-meal="onRemoveMeal"
            />
          </CalendarSlotTooltip>

          <CalendarSlotCopy
            v-if="showCopyIcon || isAddingDish || isSavingDish"
            v-show="isHoveredMealtime(day.Breakfast)"
            :isCopyingMeal="showCopyIcon"
            :isAddingDishDisabled="disableAddingDish"
            :hoveredMealtimeSlot="hoveredMealtimeSlot"
            @apply-meal="onApplyMeal(day, day.Breakfast)"
          />

          <DishesSlot
            :day="day"
            :mealtime="day.Breakfast"
            :leftovers="mealtimeLeftovers(day.leftovers, 'Breakfast')"
            @manage-recipe="manageRecipe"
          />
        </div>
        <div
          class="mealtime-slot midday"
          @mouseover="onHoveredMealtimeSlot(day, day.Lunch)"
          @mouseleave="showTooltip = false"
        >
          <span class="mealtime-tag" v-if="i === 0">
            <img class="icon-mealtime" src="@assets/images/icon-sun.svg" />
            <label>Midday</label>
          </span>

          <div
            :class="[
              'mealtime-option-bar',
              { active: showTooltip && !showCopyIcon && !isAddingDish },
            ]"
            v-if="!isPlatelistLocked && slotHasDishes(day, 'Lunch')"
          >
            <span
              class="icon-add-wrapper"
              @click="toggleTooltip(day, day.Lunch)"
              >...</span
            >
          </div>

          <CalendarSlotTooltip
            v-if="
              showTooltip && !showCopyIcon && isSelectedMealtime(day, day.Lunch)
            "
          >
            <DishesSlotTooltipList
              :day="day"
              :mealtime="day.Lunch"
              :slotHasDishes="slotHasDishes(day, 'Lunch')"
              @activate-copy="onActivateCopy"
              @remove-meal="onRemoveMeal"
            />
          </CalendarSlotTooltip>

          <CalendarSlotCopy
            v-if="showCopyIcon || isAddingDish || isSavingDish"
            v-show="isHoveredMealtime(day.Lunch)"
            :isCopyingMeal="showCopyIcon"
            :isAddingDishDisabled="disableAddingDish"
            @apply-meal="onApplyMeal(day, day.Lunch)"
          />

          <DishesSlot
            :day="day"
            :mealtime="day.Lunch"
            :leftovers="mealtimeLeftovers(day.leftovers, 'Lunch')"
            @manage-recipe="manageRecipe"
          />
        </div>
        <div
          class="mealtime-slot evening"
          @mouseover="onHoveredMealtimeSlot(day, day.Dinner)"
          @mouseleave="showTooltip = false"
        >
          <span class="mealtime-tag" v-if="i === 0">
            <img class="icon-mealtime" src="@assets/images/icon-sunset.svg" />
            <label>Evening</label>
          </span>

          <div
            :class="[
              'mealtime-option-bar',
              { active: showTooltip && !showCopyIcon && !isAddingDish },
            ]"
            v-if="!isPlatelistLocked && slotHasDishes(day, 'Dinner')"
          >
            <span
              class="icon-add-wrapper"
              @click="toggleTooltip(day, day.Dinner)"
              >...</span
            >
          </div>

          <CalendarSlotTooltip
            v-if="
              showTooltip &&
                !showCopyIcon &&
                isSelectedMealtime(day, day.Dinner)
            "
          >
            <DishesSlotTooltipList
              :day="day"
              :mealtime="day.Dinner"
              :slotHasDishes="slotHasDishes(day, 'Dinner')"
              @activate-copy="onActivateCopy"
              @remove-meal="onRemoveMeal"
            />
          </CalendarSlotTooltip>

          <CalendarSlotCopy
            v-if="showCopyIcon || isAddingDish || isSavingDish"
            v-show="isHoveredMealtime(day.Dinner)"
            :isCopyingMeal="showCopyIcon"
            :isAddingDishDisabled="disableAddingDish"
            @apply-meal="onApplyMeal(day, day.Dinner)"
          />

          <DishesSlot
            :day="day"
            :mealtime="day.Dinner"
            :leftovers="mealtimeLeftovers(day.leftovers, 'Dinner')"
            @manage-recipe="manageRecipe"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable no-descending-specificity

.plan-grid {
  background-color: white;

  .notification-program-calendar {
    left: 33.6rem;
    top: 8.4rem;

    &.with-nav-banner {
      top: 13.9rem;
    }
  }

  .plan-grid-row {
    display: flex;
    flex-wrap: nowrap;
    height: 72.4rem;
    overflow: hidden;
    padding-bottom: 0;

    &::-webkit-scrollbar {
      background: transparent;
      width: 0;
    }

    .day-col {
      border-right: 1px solid $color-light-gray;
      flex: 1;

      .day-header {
        align-items: center;
        background-color: $color-off-white;
        display: flex;
        height: 4rem;
        padding: 0 1.6rem;

        .day-num {
          letter-spacing: -1.6125px;
          margin-bottom: 0;
        }
      }

      .mealtime-slot {
        height: 22.8rem;
        overflow-y: scroll;
        padding: 0 1.2rem;
        position: relative;

        &:not(:last-child) {
          border-bottom: 1px solid $color-light-gray;
        }

        &:hover {
          .mealtime-option-bar {
            display: flex;
          }

          .icon-add-wrapper {
            background-color: $color-primary-eleven;
            border-radius: 50%;
            color: white;
            cursor: pointer;
            display: flex;
            font-size: 2rem;
            justify-content: center;
            padding: 0;
            width: 2rem;

            &:hover {
              width: 2rem;
            }

            img {
              height: 100%;
              width: 100%;
            }
          }
        }

        .mealtime-tag {
          left: 1.8rem;
          position: absolute;
        }

        .icon-mealtime {
          margin-right: 0.3rem;
        }

        .mealtime-option-bar {
          align-items: center;
          background-color: $color-action;
          display: none;
          height: 4rem;
          justify-content: flex-end;
          left: 0;
          padding: 0 1rem;
          position: absolute;
          width: 100%;
          z-index: 1;

          &.active {
            background-color: $color-primary-light;

            .icon-add-wrapper {
              background-color: $color-action;
            }
          }
        }

        .icon-add-wrapper {
          display: none;
          height: 2rem;
          width: 2rem;

          &:hover {
            width: 1.8rem;
          }

          img {
            overflow: hidden;
          }
        }

        .calendar-slot-tooltip {
          top: 3.8rem;
        }

        .copy-here-wrapper {
          top: 4.5rem;
        }
      }
    }
  }
}
</style>
